import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ExternalLink, ArrowUp } from "lucide-react";
import { BRAND_COLORS } from "@/lib/menuData";

// Footer variant types
const FOOTER_VARIANT = {
  MINIMAL: "minimal",
  FULL: "full",
  ADMIN: "admin", // simplified for admin
};

// Minimal footer (single line)
function MinimalFooter({ onOrderClick }) {
  return (
    <footer 
      data-testid="footer-minimal"
      className="py-4 px-5 transition-all duration-300 ease-in-out"
      style={{ backgroundColor: "#1a2332" }}
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center">
        <span className="text-xs sm:text-sm text-white/90">
          © {new Date().getFullYear()} Dauphin Island Food Mart
        </span>
        <span className="hidden sm:inline text-white/50">|</span>
        <a 
          href="tel:+12518612320" 
          className="text-xs sm:text-sm text-white/90 hover:text-[#3498DB] transition-colors"
        >
          (251) 861-2320
        </a>
        <span className="hidden sm:inline text-white/50">|</span>
        <Link 
          to="/hub" 
          className="text-xs sm:text-sm font-semibold hover:text-[#3498DB] transition-colors"
          style={{ color: "#3498DB" }}
          onClick={onOrderClick}
        >
          Order Online
        </Link>
      </div>
    </footer>
  );
}

// Full footer (4 columns)
function FullFooter({ className = "" }) {
  const currentYear = new Date().getFullYear();

  return (
    <footer 
      data-testid="footer-full" 
      className={`mt-auto transition-all duration-400 ease-in-out ${className}`}
      style={{ backgroundColor: "#1a2332" }}
    >
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Column 1: Restaurant Information */}
          <div>
            <h3 className="text-base font-bold text-white mb-4" style={{ fontFamily: "Montserrat" }}>
              Dauphin Island Food Mart
            </h3>
            <p className="text-sm text-gray-300 mb-1">Akshar LLC</p>
            <div className="mt-4 space-y-3">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <a 
                  href="https://maps.google.com/?q=1000+Bienville+Blvd+Dauphin+Island+AL+36528"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-300 hover:text-white transition-colors"
                >
                  1000 Bienville Blvd<br />
                  Dauphin Island, AL 36528
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <a href="tel:+12518612320" className="text-sm text-gray-300 hover:text-white transition-colors">
                  (251) 861-2320
                </a>
              </div>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="text-base font-bold text-white mb-4" style={{ fontFamily: "Montserrat" }}>
              Quick Links
            </h3>
            <nav className="space-y-2">
              <Link to="/about" className="block text-sm text-gray-300 hover:text-white transition-colors">
                About Us
              </Link>
              <Link to="/hub" className="block text-sm text-gray-300 hover:text-white transition-colors">
                Menu
              </Link>
              <Link to="/order/chicken" className="block text-sm text-gray-300 hover:text-white transition-colors">
                Order Online
              </Link>
              <Link to="/contact" className="block text-sm text-gray-300 hover:text-white transition-colors">
                Contact
              </Link>
              <Link to="/privacy-policy" className="block text-sm text-gray-300 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="block text-sm text-gray-300 hover:text-white transition-colors">
                Terms of Service
              </Link>
            </nav>
          </div>

          {/* Column 3: Hours */}
          <div>
            <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "Montserrat" }}>
              <Clock className="h-4 w-4" />
              Store Hours
            </h3>
            <div className="space-y-2 text-sm text-gray-300">
              <div className="flex justify-between">
                <span>Mon - Thu:</span>
                <span className="text-white">5:00 AM - 9:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span>Fri - Sat:</span>
                <span className="text-white">5:00 AM - 10:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span>Sunday:</span>
                <span className="text-white">5:00 AM - 8:00 PM*</span>
              </div>
              <p className="text-xs text-gray-400 mt-3 italic">
                *Summer (Apr-Sep): 9:00 PM
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Individual concepts may close earlier.<br />
                See menu for details.
              </p>
            </div>
          </div>

          {/* Column 4: Developed By */}
          <div>
            <h3 className="text-base font-bold text-white mb-4" style={{ fontFamily: "Montserrat" }}>
              Website Developed by
            </h3>
            <div className="space-y-2">
              <a 
                href="https://www.iqubeinc.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm font-semibold hover:text-white transition-colors flex items-center gap-1"
                style={{ color: BRAND_COLORS.tropicalAqua }}
              >
                IQube Technologies Inc.
                <ExternalLink className="h-3 w-3" />
              </a>
              <p className="text-xs text-gray-400 leading-relaxed">
                Empowering innovation with scalable and smart technology solutions.
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-gray-400" />
                  <a href="mailto:hr@iqubeinc.com" className="text-xs text-gray-300 hover:text-white transition-colors">
                    hr@iqubeinc.com
                  </a>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-xs text-gray-400">
                    15 Corporate Pl S, Suite 138<br />
                    Piscataway, NJ 08854
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-gray-400 text-center sm:text-left">
            © {currentYear} Dauphin Island Food Mart | Akshar LLC | All Rights Reserved
          </p>
          <p className="text-xs text-gray-400 text-center sm:text-right">
            Developed by{" "}
            <a 
              href="https://www.iqubeinc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
              style={{ color: BRAND_COLORS.tropicalAqua }}
            >
              IQube Technologies Inc.
            </a>
            {" "}| www.iqubeinc.com
          </p>
        </div>
      </div>
    </footer>
  );
}

// Admin/simplified footer (copyright only)
function AdminFooter() {
  const currentYear = new Date().getFullYear();

  return (
    <footer 
      data-testid="footer-admin"
      className="mt-auto py-4 text-center text-xs" 
      style={{ backgroundColor: "#1a2332", color: "#9CA3AF" }}
    >
      <p>© {currentYear} Dauphin Island Food Mart | Akshar LLC | All Rights Reserved</p>
      <p className="mt-1">
        Developed by{" "}
        <a href="https://www.iqubeinc.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
          IQube Technologies Inc.
        </a>
      </p>
    </footer>
  );
}

// Scroll to Top Button
function ScrollToTopButton({ visible }) {
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      data-testid="scroll-to-top-btn"
      onClick={handleScrollToTop}
      className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ease-in-out hover:scale-110 active:scale-95 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
      style={{ backgroundColor: "#3498DB" }}
      aria-label="Scroll to top"
    >
      <ArrowUp className="h-5 w-5 text-white" />
    </button>
  );
}

// Main Footer Component with smart behavior
export default function Footer({ simplified = false, variant = null }) {
  const location = useLocation();
  const [showFullFooter, setShowFullFooter] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Determine if we're on the landing page
  const isLandingPage = location.pathname === "/";
  const isAdminPage = location.pathname.startsWith("/admin") || location.pathname.startsWith("/kitchen");

  // Determine footer variant
  const getFooterVariant = useCallback(() => {
    // Explicit variant override
    if (variant) return variant;
    if (simplified) return FOOTER_VARIANT.ADMIN;

    // Admin or kitchen pages
    if (isAdminPage) return FOOTER_VARIANT.ADMIN;

    // Landing page - dynamic based on scroll
    if (isLandingPage) {
      return showFullFooter ? FOOTER_VARIANT.FULL : FOOTER_VARIANT.MINIMAL;
    }

    // All other pages - always full
    return FOOTER_VARIANT.FULL;
  }, [variant, simplified, isAdminPage, isLandingPage, showFullFooter]);

  // Handle scroll events for landing page
  useEffect(() => {
    if (!isLandingPage) {
      setShowFullFooter(false);
      return;
    }

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Calculate scroll percentage
      const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;

      // Also check if we've scrolled past the "Order Now" section (approximately 60vh from top)
      const orderNowThreshold = windowHeight * 0.6;
      const passedOrderNow = scrollTop > orderNowThreshold;

      // Show full footer if scrolled 50% OR passed order now section
      const shouldShowFull = scrollPercent >= 50 || passedOrderNow;

      setShowFullFooter(shouldShowFull);

      // Show scroll to top button after 30% scroll
      setShowScrollTop(scrollPercent >= 30);
    };

    // Initial check
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLandingPage]);

  // Handle scroll to top visibility for non-landing pages
  useEffect(() => {
    if (isLandingPage || isAdminPage) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;

      // Show scroll to top button after 30% scroll on all pages
      setShowScrollTop(scrollPercent >= 30);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLandingPage, isAdminPage]);

  const footerVariant = getFooterVariant();

  // Render based on variant
  if (footerVariant === FOOTER_VARIANT.ADMIN) {
    return <AdminFooter />;
  }

  if (footerVariant === FOOTER_VARIANT.MINIMAL) {
    return (
      <>
        <MinimalFooter />
        <ScrollToTopButton visible={showScrollTop} />
      </>
    );
  }

  // Full footer with animation for landing page transition
  return (
    <>
      <FullFooter 
        className={isLandingPage ? "animate-fade-in-up" : ""}
      />
      {!isAdminPage && <ScrollToTopButton visible={showScrollTop} />}
    </>
  );
}
